import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useBranch } from './BranchContext';
import { useOrders } from './OrderContext';
import { storage, AUTH_KEYS } from '../services/apiClient';
import { getBranchOrders } from '../services/orderService';
import SoundService from '../services/soundService';

const SOCKET_SERVER =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SOCKET_SERVER) ||
  'https://api.roll2bowl.in';
  // 'http://localhost:8080';

const SOCKET_OPTIONS = {
  path: '/api/v1/ws',
  transports: ['websocket'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 10000,
};

const HEALTHCHECK_INTERVAL_MS = 15000;

const SocketContext = createContext();

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
};

export const SocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { selectedBranch, listenAllMode, allBranches } = useBranch();
  const { refreshOrders, applyStatusUpdate } = useOrders();

  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [incomingOrder, setIncomingOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const registeredBranchRef = useRef(null);
  const registeredBranchesRef = useRef([]);
  const healthCheckTimerRef = useRef(null);
  const pendingActionsRef = useRef([]);
  const incomingOrderRef = useRef(null);
  const incomingQueueRef = useRef([]);
  const branchJoinedRef = useRef(false);
  const modalVisibleRef = useRef(false);
  const allBranchesRef = useRef(allBranches);
  const handleNewOrderRef = useRef(null);
  const handleOrderAcceptedRef = useRef(null);
  const handleOrderRejectedRef = useRef(null);
  const handleOrderStatusUpdatedRef = useRef(null);

  useEffect(() => {
    allBranchesRef.current = allBranches;
  }, [allBranches]);

  useEffect(() => {
    incomingOrderRef.current = incomingOrder;
  }, [incomingOrder]);

  useEffect(() => {
    modalVisibleRef.current = modalVisible;
  }, [modalVisible]);

  // ----- Incoming order queue helpers (FIFO) -----
  const getOrderId = (order) => order?._id || order?.orderId || order?.id;

  const enqueueIncomingOrder = useCallback((order) => {
    const id = getOrderId(order);
    if (!id) return;
    if (incomingOrderRef.current && getOrderId(incomingOrderRef.current) === id) return;
    if (incomingQueueRef.current.some((o) => getOrderId(o) === id)) return;
    incomingQueueRef.current.push(order);
    // Start the persistent alarm (sound + browser notification + title flash)
    // so a backgrounded tab still surfaces the order until it's handled.
    SoundService.startOrderAlarm(id, order);
    if (!incomingOrderRef.current) {
      const next = incomingQueueRef.current.shift();
      if (next) {
        incomingOrderRef.current = next;
        setIncomingOrder(next);
        setModalVisible(true);
      }
    }
  }, []);

  const removeQueuedOrderById = useCallback((orderId) => {
    if (!orderId) return;
    incomingQueueRef.current = incomingQueueRef.current.filter((o) => getOrderId(o) !== orderId);
  }, []);

  const showNextQueuedOrder = useCallback((force = false) => {
    if (!force && (incomingOrderRef.current || modalVisibleRef.current)) return;
    const next = incomingQueueRef.current.shift();
    if (next) {
      incomingOrderRef.current = next;
      setIncomingOrder(next);
      setModalVisible(true);
    }
  }, []);

  /** Hydrate pending orders from API for listen-all mode */
  const hydratePendingOrdersFromApi = useCallback(async () => {
    const branches = allBranchesRef.current || allBranches;
    if (!branches || branches.length === 0) return;
    try {
      const PENDING_STATUSES = ['Placed', 'Pending'];
      const branchOrderPromises = branches.map((b) =>
        getBranchOrders(b._id).catch(() => ({ orders: [] }))
      );
      const responses = await Promise.all(branchOrderPromises);
      const allPending = [];
      responses.forEach((res, idx) => {
        const list = res?.orders || [];
        const branch = branches[idx];
        list.forEach((o) => {
          if (PENDING_STATUSES.includes(o?.status)) {
            allPending.push({
              ...o,
              branchId: o.branch?._id || o.branch || branch?._id,
              branchName: o.branch?.name || branch?.name,
              customerName: o.customer?.name || 'Customer',
              items: (o.items || []).map((it) => ({
                name: it.foodItem?.name || it.name || 'Item',
                quantity: it.quantity,
                price: it.price,
              })),
              subTotal: o.subTotal,
              payoutAmount: o.grandTotal ?? o.subTotal,
              orderId: o.orderId || o._id,
            });
          }
        });
      });
      allPending.sort((a, b) => {
        const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tA - tB;
      });
      allPending.forEach((order) => enqueueIncomingOrder(order));
      if (allPending.length > 0) {
        
      }
    } catch (e) {
      
    }
  }, [allBranches, enqueueIncomingOrder]);

  // Register the branch with server
  const registerBranch = useCallback((branchId) => {
    if (!socketRef.current || !branchId) return;
    const s = socketRef.current;
    const roomUnderscore = `branch_${branchId}`;
    const roomColon = `branch:${branchId}`;
    try {
      
      s.emit('register_branch', { branchId });
      s.emit('join_branch', { branchId });
      s.emit('join_room', { room: roomUnderscore });
      s.emit('join_room', { room: roomColon });
      s.emit('join_room', { room: branchId });
    } catch (e) {
      
    }
  }, []);

  // Register all branches for listen-all mode
  const registerAllBranches = useCallback(() => {
    const branches = allBranchesRef.current || allBranches;
    if (!socketRef.current || !listenAllMode || !branches || branches.length === 0) return;
    

    const branchIds = branches.map(b => b._id).filter(Boolean);
    registeredBranchesRef.current = branchIds;
    branchIds.forEach(branchId => registerBranch(branchId));
  }, [listenAllMode, registerBranch]);

  // Accept order
  const acceptOrder = useCallback(async (orderId, orderBranchId) => {
    const s = socketRef.current;
    const branchId = orderBranchId || selectedBranch?._id || registeredBranchRef.current;
    if (!s || !s.connected) {
      pendingActionsRef.current.push({ type: 'accept', orderId, branchId, ts: Date.now() });
      return { success: true, queued: true };
    }
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve({ success: false, message: 'accept timeout' });
        }
      }, 8000);
      s.emit('accept_order', { orderId, branchId });
      const handler = (res) => {
        s.off('accept_order_response', handler);
        if (!resolved) {
          clearTimeout(timeout);
          resolved = true;
          resolve(res);
        }
      };
      s.on('accept_order_response', handler);
    });
  }, [selectedBranch]);

  // Reject order
  const rejectOrder = useCallback(async (orderId, reason = 'Branch unavailable', orderBranchId) => {
    const s = socketRef.current;
    const branchId = orderBranchId || selectedBranch?._id || registeredBranchRef.current;
    if (!s || !s.connected) {
      pendingActionsRef.current.push({ type: 'reject', orderId, reason, branchId, ts: Date.now() });
      return { success: true, queued: true };
    }
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve({ success: false, message: 'reject timeout' });
        }
      }, 8000);
      s.emit('reject_order', { orderId, reason, branchId });
      const handler = (res) => {
        s.off('reject_order_response', handler);
        if (!resolved) {
          clearTimeout(timeout);
          resolved = true;
          resolve(res);
        }
      };
      s.on('reject_order_response', handler);
    });
  }, [selectedBranch]);

  const flushPendingActions = useCallback(() => {
    const s = socketRef.current;
    if (!s || !s.connected) return;
    const queue = pendingActionsRef.current;
    if (!queue || queue.length === 0) return;
    queue.sort((a, b) => a.ts - b.ts);
    pendingActionsRef.current = [];
    queue.forEach((action) => {
      const branchId = action.branchId || selectedBranch?._id || registeredBranchRef.current;
      if (action.type === 'accept') {
        s.emit('accept_order', { orderId: action.orderId, branchId });
      } else if (action.type === 'reject') {
        s.emit('reject_order', { orderId: action.orderId, reason: action.reason || 'Branch unavailable', branchId });
      }
    });
  }, [selectedBranch]);

  const handleNewOrder = useCallback((order) => {
    // Alarm (sound + notification + title flash) is started inside enqueueIncomingOrder.
    enqueueIncomingOrder(order);
    try { refreshOrders(); } catch (e) {  }
  }, [refreshOrders, enqueueIncomingOrder]);

  const handleOrderAccepted = useCallback((payload) => {

    const id = payload?.orderId;
    SoundService.stopOrderAlarm(id);
    if (incomingOrderRef.current && id && getOrderId(incomingOrderRef.current) === id) {
      setModalVisible(false);
      incomingOrderRef.current = null;
      setIncomingOrder(null);
      showNextQueuedOrder(true);
    } else {
      removeQueuedOrderById(id);
    }
    try {
      const v = payload?.updatedAt ? new Date(payload.updatedAt).getTime() : Date.now();
      applyStatusUpdate(id, 'Accepted', v);
    } catch (e) {  }
    try { refreshOrders(); } catch (e) {  }
  }, [applyStatusUpdate, refreshOrders, showNextQueuedOrder, removeQueuedOrderById]);

  const handleOrderRejected = useCallback((payload) => {

    const id = payload?.orderId;
    SoundService.stopOrderAlarm(id);
    if (incomingOrderRef.current && id && getOrderId(incomingOrderRef.current) === id) {
      setModalVisible(false);
      incomingOrderRef.current = null;
      setIncomingOrder(null);
      showNextQueuedOrder(true);
    } else {
      removeQueuedOrderById(id);
    }
    try {
      const v = payload?.updatedAt ? new Date(payload.updatedAt).getTime() : Date.now();
      applyStatusUpdate(id, 'Rejected', v);
    } catch (e) {  }
    try { refreshOrders(); } catch (e) {  }
  }, [applyStatusUpdate, refreshOrders, showNextQueuedOrder, removeQueuedOrderById]);

  const handleOrderStatusUpdated = useCallback((payload) => {
    
    const { orderId, status, updatedAt } = payload || {};
    if (orderId && status) {
      if (status === 'Accepted' || status === 'Rejected' || status === 'Cancelled') {
        SoundService.stopOrderAlarm(orderId);
        if (incomingOrderRef.current && orderId && getOrderId(incomingOrderRef.current) === orderId) {
          setModalVisible(false);
          incomingOrderRef.current = null;
          setIncomingOrder(null);
          showNextQueuedOrder(true);
        } else {
          removeQueuedOrderById(orderId);
        }
      }
      try {
        const v = updatedAt ? new Date(updatedAt).getTime() : Date.now();
        applyStatusUpdate(orderId, status, v);
      } catch (e) {  }
    }
    try { refreshOrders(); } catch (e) {  }
  }, [applyStatusUpdate, refreshOrders, showNextQueuedOrder, removeQueuedOrderById]);

  // Keep refs updated with latest callbacks so socket listeners always use current versions
  useEffect(() => { handleNewOrderRef.current = handleNewOrder; }, [handleNewOrder]);
  useEffect(() => { handleOrderAcceptedRef.current = handleOrderAccepted; }, [handleOrderAccepted]);
  useEffect(() => { handleOrderRejectedRef.current = handleOrderRejected; }, [handleOrderRejected]);
  useEffect(() => { handleOrderStatusUpdatedRef.current = handleOrderStatusUpdated; }, [handleOrderStatusUpdated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!listenAllMode && (!selectedBranch || !selectedBranch._id)) return;

    const token = storage.get(AUTH_KEYS.token);
    if (!token) {
      
      return;
    }

    if (!socketRef.current) {
      const socket = io(SOCKET_SERVER, {
        ...SOCKET_OPTIONS,
        auth: { token, branchId: selectedBranch?._id },
        extraHeaders: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        
        setConnected(true);
        if (listenAllMode) {
          registerAllBranches();
          branchJoinedRef.current = false;
          setTimeout(() => hydratePendingOrdersFromApi(), 800);
        } else if (selectedBranch && selectedBranch._id) {
          registerBranch(selectedBranch._id);
          branchJoinedRef.current = false;
        }
        flushPendingActions();
      });

      socket.on('disconnect', (reason) => {
        
        setConnected(false);
      });

      socket.on('connect_error', (err) => {
        
      });

      socket.on('error', (err) => {
        
      });

      socket.on('branch_registered', (data) => {
        
        if (data?.branchId) registeredBranchRef.current = data.branchId;
        if (data?.branchId === selectedBranch?._id || data?.joined || data?.success) {
          branchJoinedRef.current = true;
        }
      });

      socket.on('joined_branch', (data) => {
        
        if (data?.branchId) registeredBranchRef.current = data.branchId;
        if (data?.branchId === selectedBranch?._id || data?.joined || data?.success) {
          branchJoinedRef.current = true;
        }
      });

      socket.on('joined_room', (data) => {
        
        if (selectedBranch?._id) registeredBranchRef.current = selectedBranch._id;
        branchJoinedRef.current = true;
      });

      socket.on('new_order', (order) => {
        
        if (handleNewOrderRef.current) handleNewOrderRef.current(order);
      });
      socket.on('order_accepted', (payload) => {
        if (handleOrderAcceptedRef.current) handleOrderAcceptedRef.current(payload);
      });
      socket.on('order_rejected', (payload) => {
        if (handleOrderRejectedRef.current) handleOrderRejectedRef.current(payload);
      });
      socket.on('order_status_updated', (payload) => {
        if (handleOrderStatusUpdatedRef.current) handleOrderStatusUpdatedRef.current(payload);
      });

      socket.onAny((event, ...args) => {
        if (!['new_order', 'order_accepted', 'order_rejected', 'order_status_updated', 'branch_registered', 'joined_branch', 'joined_room', 'connect', 'disconnect', 'reconnect', 'connect_error', 'error'].includes(event)) {
          
        }
      });

      

      socket.on('reconnect', (attempt) => {
        
        setConnected(true);
        if (listenAllMode) {
          registerAllBranches();
          setTimeout(() => hydratePendingOrdersFromApi(), 800);
        } else if (registeredBranchRef.current) {
          registerBranch(registeredBranchRef.current);
        } else if (selectedBranch && selectedBranch._id) {
          registerBranch(selectedBranch._id);
        }
        flushPendingActions();
      });

      if (!healthCheckTimerRef.current) {
        healthCheckTimerRef.current = setInterval(() => {
          const s = socketRef.current;
          if (!s) return;
          if (!s.connected) {
            try { s.connect(); } catch (e) { }
            return;
          }
          if (listenAllMode) {
            const branches = allBranchesRef.current || allBranches;
            if (branches && branches.length > 0) {
              registerAllBranches();
            }
          } else {
            const targetBranch = selectedBranch?._id || registeredBranchRef.current;
            if (targetBranch && (!branchJoinedRef.current || registeredBranchRef.current !== targetBranch)) {
              registerBranch(targetBranch);
            }
          }
        }, HEALTHCHECK_INTERVAL_MS);
      }
    }

    return () => {
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
    };
  }, [isAuthenticated, selectedBranch?._id, listenAllMode, registerBranch, registerAllBranches, flushPendingActions, hydratePendingOrdersFromApi]);

  useEffect(() => {
    const s = socketRef.current;
    if (!s || !s.connected) return;

    if (listenAllMode) {
      registerAllBranches();
      branchJoinedRef.current = false;
      setTimeout(() => hydratePendingOrdersFromApi(), 800);
    } else if (selectedBranch && selectedBranch._id) {
      if (s.auth?.branchId !== selectedBranch._id) {
        s.auth = { ...(s.auth || {}), branchId: selectedBranch._id };
        try {
          s.disconnect();
          s.connect();
        } catch (e) { }
      }
      branchJoinedRef.current = false;
      registerBranch(selectedBranch._id);
      registeredBranchRef.current = selectedBranch._id;
    }
  }, [selectedBranch, listenAllMode, registerBranch, registerAllBranches, hydratePendingOrdersFromApi]);

  useEffect(() => {
    if (!isAuthenticated && socketRef.current) {
      try { socketRef.current.disconnect(); } catch (e) { }
      socketRef.current = null;
      setConnected(false);
      setModalVisible(false);
      setIncomingOrder(null);
      registeredBranchRef.current = null;
      branchJoinedRef.current = false;
      if (healthCheckTimerRef.current) {
        clearInterval(healthCheckTimerRef.current);
        healthCheckTimerRef.current = null;
      }
    }
  }, [isAuthenticated]);

  const value = {
    socket: socketRef.current,
    connected,
    incomingOrder,
    modalVisible,
    setModalVisible,
    acceptOrder,
    rejectOrder,
    showNextQueuedOrder,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
